import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { profileService } from '../../services/profileService';
import { departmentService } from '../../services/departmentService';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { 
  ROLE_LABELS, 
  ROLE_COLORS,
  getSectorLabel
} from '../../constants/authConstants';
import { dateUtils } from '../../utils/helpers';

const ProfileInfo = ({ profileData, canEdit, isOwnProfile, onUpdate }) => {
  const { user: currentUser } = useAuth();
  const { isMobile, isTablet, getResponsiveValue } = useTheme();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    fullName: profileData.fullName || '',
    phoneNumber: profileData.phoneNumber || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const sectionSpacing = getResponsiveValue({
    mobile: 'space-y-4',
    desktop: 'space-y-6'
  });

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-4',
    desktop: 'gap-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-base',
    desktop: 'text-lg'
  });

  const subtitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const sectionTitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const labelSize = getResponsiveValue({
    mobile: 'text-xs',
    desktop: 'text-xs'
  });

  const textSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-2',
    desktop: 'flex-row space-x-2'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'w-full',
    desktop: 'w-auto'
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const allDepartments = await departmentService.getAllDepartments();
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const getDepartmentLabel = (departmentCode) => {
    if (!departments || departments.length === 0) return departmentCode;
    
    const dept = departments.find(d => d.departmentCode === departmentCode);
    return dept ? dept.departmentName : departmentCode;
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditData({
        fullName: profileData.fullName || '',
        phoneNumber: profileData.phoneNumber || ''
      });
      setErrors({});
    }
    setIsEditing(!isEditing);
    setSuccess(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    if (success) setSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!editData.fullName || !editData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (editData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (editData.phoneNumber) {
      const cleanedPhone = editData.phoneNumber.replace(/\D/g, '');
      const phonePattern = /^[6-9]\d{9}$/;
      if (!phonePattern.test(cleanedPhone)) {
        newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setErrors({});
      
      const updatedData = await profileService.updateProfileInfo(profileData.id, {
        fullName: editData.fullName,
        phoneNumber: editData.phoneNumber
      });
      
      onUpdate(updatedData);
      
      setIsEditing(false);
      setSuccess(true);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className={containerPadding}>
      {success && (
        <div className="mb-6 bg-success-subtle border border-border-primary rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className={`${textSize} font-medium text-success`}>Profile updated successfully!</span>
          </div>
        </div>
      )}

      <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'items-center justify-between'} mb-6`}>
        <div>
          <h3 className={`${titleSize} font-semibold text-text-primary`}>Profile Information</h3>
          <p className={`${subtitleSize} text-text-tertiary`}>
            {isEditing ? 'Edit your personal information' : 'View your personal information'}
          </p>
        </div>
        
        {canEdit && (
          <div className={`flex ${buttonSpacing}`}>
            {isEditing && (
              <Button
                onClick={handleEditToggle}
                variant="outline"
                size="sm"
                disabled={loading}
                className={buttonSize}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={isEditing ? handleSave : handleEditToggle}
              size="sm"
              loading={loading}
              disabled={loading}
              className={buttonSize}
            >
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </div>
        )}
      </div>

      {errors.general && (
        <div className="mb-6 bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className={`${textSize} text-error`}>{errors.general}</span>
          </div>
        </div>
      )}

      <div className={sectionSpacing}>
        <div>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Personal Information
          </h4>
          
          <div className={`grid ${gridCols} ${gridGap}`}>
            <div>
              {isEditing ? (
                <Input
                  label="Full Name"
                  type="text"
                  value={editData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={errors.fullName}
                  placeholder="Enter your full name"
                  className={textSize}
                />
              ) : (
                <div>
                  <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                    Full Name
                  </label>
                  <p className={`${textSize} text-text-primary mt-1`}>
                    {profileData.fullName || <span className="text-text-disabled italic">Not provided</span>}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Email Address
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>{profileData.email}</p>
              <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-quaternary mt-1`}>Email cannot be changed</p>
            </div>

            <div>
              {isEditing ? (
                <Input
                  label="Phone Number"
                  type="tel"
                  value={editData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  error={errors.phoneNumber}
                  placeholder="Enter your phone number"
                  className={textSize}
                />
              ) : (
                <div>
                  <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                    Phone Number
                  </label>
                  <p className={`${textSize} text-text-primary mt-1`}>
                    {profileData.phoneNumber ? 
                      formatPhoneNumber(profileData.phoneNumber) : 
                      <span className="text-text-disabled italic">Not provided</span>
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-border-secondary pt-6">
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            StateSync Government Information
          </h4>
          
          <div className={`grid ${gridCols} ${gridGap}`}>
            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Sector
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>
                {profileData.sector ? getSectorLabel(profileData.sector) : 
                 <span className="text-text-disabled italic">Not assigned</span>}
              </p>
              <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-quaternary mt-1`}>
                Assigned during registration
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Department
              </label>
              {loadingDepartments ? (
                <p className={`${textSize} text-text-tertiary mt-1`}>Loading...</p>
              ) : (
                <p className={`${textSize} text-text-primary mt-1`}>
                  {profileData.department ? getDepartmentLabel(profileData.department) : 
                   <span className="text-text-disabled italic">Not assigned</span>}
                </p>
              )}
              <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-quaternary mt-1`}>
                Assigned during registration
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Role
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  ROLE_COLORS[profileData.role] || 'bg-blue-100 text-blue-800'
                }`}>
                  {ROLE_LABELS[profileData.role] || profileData.role}
                </span>
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Account Status
              </label>
              <p className={`${textSize} text-text-primary mt-1 capitalize`}>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  profileData.status === 'active' ? 'bg-success-subtle text-success' :
                  profileData.status === 'pending' ? 'bg-warning-subtle text-warning' :
                  profileData.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                  profileData.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                  'bg-surface-secondary text-text-secondary'
                }`}>
                  {profileData.status}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-border-secondary pt-6">
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Account Information
          </h4>
          
          <div className={`grid ${gridCols} ${gridGap}`}>
            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Member Since
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>
                {profileData.createdAt ? 
                  dateUtils.formatDate(profileData.createdAt) : 
                  'Unknown'
                }
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Last Login
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>
                {profileData.lastLoginAt ? 
                  dateUtils.formatDate(profileData.lastLoginAt) : 
                  <span className="text-text-disabled italic">Never logged in</span>
                }
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Profile Updated
              </label>
              <p className={`${textSize} text-text-primary mt-1`}>
                {profileData.profileUpdatedAt ? 
                  dateUtils.formatDate(profileData.profileUpdatedAt) : 
                  <span className="text-text-disabled italic">Never updated</span>
                }
              </p>
            </div>

            <div>
              <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                Account ID
              </label>
              <p className={`${textSize} text-text-secondary mt-1 font-mono text-xs`}>
                {profileData.id || profileData.uid || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileInfo;